import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Grid,
  IconButton,
  List,
  ListItemButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  LinearProgress,
  AlertTitle,
  Alert,
  Snackbar,
  Chip,
  TextField,
} from "@mui/material";
import OutboxIcon from "@mui/icons-material/Outbox";
import CloseIcon from "@mui/icons-material/Close"; //
import { useDispatch, useSelector } from "react-redux";
import { appSelector } from "../../redux/selector";
import {
  assignCandidateToRecruiterAction,
  getActiveCandidatesAction,
  getAssignedCandidatesAction,
  removeAssignedCandidateAction,
  resetCandidatesAction,
  selectedRecruiterAction,
} from "../../redux/action";
import { userSelector } from "../../user-redux/selector";

const getBackgroundColor = (index) => {
  const colors = [
    "rgb(234 193 184)",
    "rgb(162 195 168)",
    "rgb(144 154 202)",
    "rgb(121 154 202)",
    "rgb(104 174 202)",
  ];
  return colors[index % colors.length];
};

const AdminRecruitersList = ({ searchTerm, onSearchChange }) => {
  const dispatch = useDispatch();
  const {
    myRecruiters,
    selectedRecruiter,
    activeCandidates,
    assignedCandidates,
  } = useSelector(appSelector);
  const { user } = useSelector(userSelector);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [allocatedRecruiter, setAllocatedRecruiter] = useState("");
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isDesktopSmall = useMediaQuery(theme.breakpoints.between("md", "lg")); // Small desktops
  const isDesktopLarge = useMediaQuery(theme.breakpoints.up("lg"));
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(searchTerm); // State for search input

  const handleClick = (recruiter) => {
    dispatch(selectedRecruiterAction(recruiter));
  };
  const handleSubmit = async () => {
    if (selectedCandidates?.length > 0) {
      const allocatedCandidatesData = {
        candidate_ids: selectedCandidates,
        user_id: user?.usertype == 13 ? user?.admin_id : user?.id,
        recruiter_id: selectedRecruiter?.recruiter_id,
      };
      const userId = user?.usertype == 13 ? user?.admin_id : user?.id;
      await dispatch(
        assignCandidateToRecruiterAction(allocatedCandidatesData, userId)
      );
      setOpenDialog(false);
    } else {
      setSnackbarOpen(true);
    }
  };
  const handleDialogOpen = async () => {
    setLoading(true);
    setAllocatedRecruiter("");
    setSelectedCandidates([]);
    setOpenDialog(true);

    await dispatch(resetCandidatesAction());
    setLoading(true);
    await dispatch(getActiveCandidatesAction(user?.id, 1, 200));
    setLoading(false);
  };
  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const hoverIconStyles = {
    height: isDesktopLarge ? "20px" : isDesktopSmall ? "16px" : "13px",
    width: isDesktopLarge ? "20px" : isDesktopSmall ? "16px" : "13px",
    color: "#616161",
    "&:hover": {
      bgcolor: "#F5F5F5",
      color: "#424242",
    },
  };
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };
  // Filter recruiters based on search term
  const filteredRecruiters = myRecruiters?.filter((recruiter) =>
    recruiter.recruiter_name.toLowerCase().startsWith(searchTerm.toLowerCase())
  );

  // const unassignedCandidates = activeCandidates?.filter(candidate =>
  //   !assignedCandidates.includes(candidate.candidate_id)
  // );
  const assignedCandidateIds = assignedCandidates.map(
    (candidate) => candidate.candidate_id
  );
  const unassignedCandidates = activeCandidates.filter(
    (candidate) => !assignedCandidateIds.includes(candidate.candidate_id)
  );

  const filteredCandidates = unassignedCandidates?.filter((candidate) =>
    candidate.candidate_name.toLowerCase().startsWith(searchValue.toLowerCase())
  );

  const dialogHeight = filteredCandidates.length <= 10 ? "700px" : "700px"; // Example heights
  const handleRemoveCandidate = (candidateId) => {
    const candidateData = {
      candidate_id: candidateId,
      recruiter_id: selectedRecruiter?.recruiter_id,
    };
    const assignedData = {
      recruiter_id: selectedRecruiter?.recruiter_id,
    };
    dispatch(removeAssignedCandidateAction(candidateData, assignedData));
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Paper
          elevation={2}
          style={{ height: "calc(96vh - 48px)", overflow: "scroll" }}
        >
          <List>
            {filteredRecruiters?.length > 0 ? (
              filteredRecruiters.map((recruiter, index) => (
                <ListItemButton
                  key={index}
                  onClick={() => handleClick(recruiter)}
                  sx={{
                    backgroundColor:
                      recruiter?.recruiter_id ===
                      selectedRecruiter?.recruiter_id
                        ? "rgba(25, 118, 210, 0.08)"
                        : "inherit",
                    "&:hover .more-icon": {
                      display: "flex", // Changed from 'block' to 'flex' for alignment
                    },
                    position: "relative", // Ensure that the icon is positioned relative to the button
                  }}
                  onMouseOver={() => setHoverIndex(index)}
                  onMouseLeave={() => setHoverIndex(null)}
                >
                  <Grid container spacing={5}>
                    <Grid item xs={2}>
                      <Box>
                        <Avatar
                          style={{
                            backgroundColor: getBackgroundColor(index),
                            textTransform: "capitalize",
                          }}
                        >
                          {recruiter?.recruiter_name?.[0]}
                        </Avatar>
                      </Box>
                    </Grid>
                    <Grid item xs={10} sx={{ p: 1, cursor: "pointer" }}>
                      <Stack direction="column" spacing={1}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "3px",
                          }}
                        >
                          <Typography
                            paragraph
                            sx={{
                              mb: 0,
                              fontWeight: 700,
                              fontSize: "12px",
                              minWidth: isDesktopLarge ? "150px" : "100px",
                              maxWidth: isDesktopLarge ? "150px" : "100px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              cursor: "pointer",
                            }}
                          >
                            {recruiter.recruiter_name}
                          </Typography>
                          <Box
                            className="more-icon"
                            sx={{
                              display: "none", // Hidden by default
                              position: "absolute", // Positioned absolutely within the ListItemButton
                              right: "10px", // Adjust as needed
                              top: "50%", // Adjust as needed
                              transform: "translateY(-50%)", // Center vertically
                            }}
                          >
                            <Tooltip followCursor title="Allocation">
                              <IconButton onClick={handleDialogOpen}>
                                <OutboxIcon sx={hoverIconStyles} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Stack>
                      <Typography variant="body2" style={{ fontSize: "11px" }}>
                        {recruiter.role}
                      </Typography>
                    </Grid>
                  </Grid>
                </ListItemButton>
              ))
            ) : (
              <Typography variant="body1" style={{ padding: "16px" }}>
                No recruiters found
              </Typography>
            )}
          </List>
        </Paper>
        <Dialog
          open={openDialog}
          onClose={handleDialogClose}
          fullWidth
          maxWidth="sm"
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: "12px",
              padding: "20px",
            },
            height: dialogHeight, // Apply dynamic height
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "left",
              fontWeight: "bold",
              color: "#406882",
              padding: "0px",
              marginTop: "10px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextField
                label="Search Candidate Name"
                variant="outlined"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                fullWidth
                sx={{
                  borderRadius: "5px",
                  fontSize: "15px",
                  padding: "5px",
                }}
              />

              <Tooltip title="Clear">
                <CloseIcon
                  onClick={() => setSearchValue("")}
                  style={{
                    position: "absolute",
                    top: "10%",
                    right: "30px",
                    transform: "translateY(-60%)",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            </Box>
          </DialogTitle>
          {/* <Divider /> */}
          <DialogContent style={{ padding: "0" }}>
            <Box component="form" noValidate autoComplete="off" sx={{}}>
              {loading ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  mt={2}
                  p={5}
                >
                  <Alert
                    severity="info"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Box p={1}>
                      <AlertTitle>Fetching Active Candidates</AlertTitle>
                      We are fetching the Candidates. Please wait.......
                    </Box>
                    <LinearProgress style={{ width: "100%" }} />
                  </Alert>
                </Box>
              ) : (
                <>
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: "500", my: "5px" }}
                    >
                      Assigned Candidates
                    </Typography>
                    {assignedCandidates?.length > 0 && (
                      <Box>
                        {assignedCandidates
                        .filter((candidate =>
                          candidate.status == 1
                        ))
                        .map((candidate, index) => (
                          <Chip
                            key={index}
                            label={candidate.candidate_name}
                            sx={{ margin: "4px", backgroundColor: "aliceblue" }}
                            icon={<CloseIcon style={{ fontSize: "15px" }} />}
                            onClick={() =>
                              handleRemoveCandidate(candidate?.candidate_id)
                            }
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Box>
                    {/* <Typography variant="body1" sx={{ fontWeight: "500", my: "5px", }}>
                        Active Candidates
                      </Typography> */}

                    {filteredCandidates?.length > 0 &&
                      filteredCandidates.map((candidate, value) => (
                        <Box
                          key={value}
                          sx={{
                            mb: 0,
                            display: "flex",
                            flexDirection: "column", // Stack the entire container vertically
                            padding: "5px",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center", // Align items vertically centered
                              mb: 0, // Add margin bottom to create space between rows
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedCandidates?.includes(
                                    candidate.candidate_id
                                  )}
                                  onChange={() => {
                                    setSelectedCandidates(
                                      selectedCandidates.includes(
                                        candidate.candidate_id
                                      )
                                        ? selectedCandidates.filter(
                                            (id) =>
                                              id !== candidate.candidate_id
                                          )
                                        : [
                                            ...selectedCandidates,
                                            candidate.candidate_id,
                                          ]
                                    );
                                  }}
                                  value={candidate.candidate_id}
                                  color="primary"
                                  sx={{ fontSize: "12px" }}
                                />
                              }
                              // label={candidate.candidate_name}
                            />
                            <Box sx={{ padding: "0" }}>
                              {" "}
                              {/* Add margin-left to separate the checkbox from the text */}
                              <Typography variant="body1" color="textprimary">
                                {candidate.candidate_name}
                              </Typography>
                              <Typography
                                variant="body"
                                color="textSecondary"
                                style={{ fontSize: "12px" }}
                              >
                                {candidate.role}
                              </Typography>
                            </Box>
                          </Box>
                          <Divider style={{ color: "lightgray" }} />
                        </Box>
                      ))}
                  </Box>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Submit
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity="warning"
            sx={{ width: "100%" }}
          >
            Please Select Atleast One Candidate
          </Alert>
        </Snackbar>
      </Grid>
    </Grid>
  );
};

export default AdminRecruitersList;
